import { Link } from '@tanstack/react-router';
import { ArrowLeftRight, Baby, CalendarClock, Flag, Repeat, Star } from 'lucide-react';
import type { Schemas } from '../../api/client';
import { useMembers } from '../../api/queries';
import { useHousehold } from '../../api/session';
import { Avatar, Badge, Button, Card } from '../../components/ui';
import { dueLabel, priorityTone } from '../../lib/format';
import { ParentLink, SubtaskProgress } from './Subtasks';
import { primaryAction, useTaskCommands } from './task-actions';

type Task = Schemas['TaskResponse'];

export function TaskCard({ task, hideParent = false }: { task: Task; hideParent?: boolean }) {
  const { memberId } = useHousehold();
  const members = useMembers().data ?? [];
  const name = (id: string) => members.find((m) => m.id === id)?.displayName ?? 'someone';
  const { simple, complete } = useTaskCommands();
  const primary = primaryAction(task.allowedActions);
  const due = dueLabel(task);
  const busy = simple.isPending || complete.isPending;

  const run = () => {
    if (!primary) return;
    if (primary.action === 'complete') complete.mutate({ taskId: task.id });
    else if (primary.action !== 'claim-and-complete') simple.mutate({ action: primary.action, taskId: task.id });
  };

  return (
    <Card className="flex items-start gap-3">
      <div className="min-w-0 flex-1">
        <Link to="/tasks/$taskId" params={{ taskId: task.id }} className={`block font-medium hover:underline ${task.status === 'COMPLETED' ? 'text-[var(--muted)] line-through' : ''}`}>
          {task.title}
        </Link>
        {!hideParent && task.parent ? <ParentLink parent={task.parent} /> : null}
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-[var(--muted)]">
          <Badge tone={priorityTone(task.priority.code)}>
            <Flag size={11} /> {task.priority.label}
          </Badge>
          {due ? (
            <Badge tone={task.overdue ? 'danger' : task.dueToday ? 'accent' : 'neutral'}>
              <CalendarClock size={11} /> {due}
            </Badge>
          ) : null}
          {task.seriesId ? (
            <Badge>
              <Repeat size={11} /> Recurring
            </Badge>
          ) : null}
          {task.points ? (
            <Badge tone="accent">
              <Star size={11} /> {task.points}
            </Badge>
          ) : null}
          {task.pending.transfer ? (
            <Badge tone="info">
              <ArrowLeftRight size={11} />
              {task.pending.transfer.toMemberId === memberId ? 'Asked you to take it' : `Transfer to ${name(task.pending.transfer.toMemberId)} requested`}
            </Badge>
          ) : null}
          {task.pending.delegation ? (
            <Badge tone="info">
              <Baby size={11} /> Proposed for {name(task.pending.delegation.childMemberId)}
            </Badge>
          ) : null}
          {task.pending.priorityRequest ? <Badge tone="warn">Priority under discussion</Badge> : null}
          <SubtaskProgress progress={task.subtasks} compact />
        </div>
        <div className="mt-2 flex items-center gap-1.5 text-xs text-[var(--muted)]">
          {task.assignee ? (
            <>
              <Avatar name={task.assignee.displayName} size={18} />
              {task.assignee.memberId === memberId ? 'You' : task.assignee.displayName}
            </>
          ) : (
            <span>House Pool</span>
          )}
        </div>
      </div>
      {primary && task.status !== 'COMPLETED' ? (
        <Button size="sm" variant={primary.action === 'claim' ? 'primary' : 'secondary'} busy={busy} onClick={run}>
          {primary.label}
        </Button>
      ) : null}
    </Card>
  );
}

export function TaskList({ tasks, empty }: { tasks: Task[] | undefined; empty: React.ReactNode }) {
  if (!tasks) return null;
  if (!tasks.length) return <>{empty}</>;
  return (
    <ul className="space-y-2.5">
      {tasks.map((t) => (
        <li key={t.id}>
          <TaskCard task={t} />
        </li>
      ))}
    </ul>
  );
}
